import { useMutation, useQuery } from '@apollo/client';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import { useCallback, useState } from 'react';

import { gengql } from '@/__generated__';
import { BillingInterval, SubscriptionStatus } from '@/__generated__/graphql';
import Tooltip from '@/componentsv2/Tooltip';
import config from '@/config';
import { money } from '@/core/money';
import { darkGreen, red500 } from '@/theme/colors';
import { formatDate } from '@/utils';

import Button from '../Button';
import Dialog from '../Dialog';
import Link from '../Link';
import StripeReceiptList from '../StripeReceiptList/StripeReceiptList';
import TransactionList from '../TransactionList';
import ManageSeatsModal from './ManageSeatsModal';
import s from './SubscriptionSettings.module.scss';

const GET_SUBSCRIPTION_PRICES = gengql(/* GraphQL */ `
  query getSubscriptionPrices {
    subscriptionPrices {
      monthly {
        currency
        interval
        priceId
        unitAmountCents
      }
      annual {
        currency
        interval
        priceId
        unitAmountCents
      }
    }
  }
`);

const UPDATE_SUBSCRIPTION_INTERVAL_MUTATION = gengql(/* GraphQL */ `
  mutation updateSubscriptionInterval($subscriptionId: String!, $interval: BillingInterval!) {
    updateSubscriptionInterval(subscriptionId: $subscriptionId, interval: $interval) {
      id
      status
      billingInterval
      quantity
      pricePerSeat {
        cents
        currency
      }
      totalPrice {
        cents
        currency
      }
      currentPeriodEnd
      trialEnd
      isTrialing
      daysUntilTrialEnd
      daysUntilRenewal
      cancelAtPeriodEnd
      isActive
    }
  }
`);

const REACTIVATE_SUBSCRIPTION_MUTATION = gengql(/* GraphQL */ `
  mutation reactivateSub($subscriptionId: String!) {
    reactivateSubscription(subscriptionId: $subscriptionId) {
      id
      status
      billingInterval
      quantity
      pricePerSeat {
        cents
        currency
      }
      totalPrice {
        cents
        currency
      }
      currentPeriodEnd
      trialEnd
      isTrialing
      daysUntilTrialEnd
      daysUntilRenewal
      cancelAtPeriodEnd
      isActive
    }
  }
`);

const CANCEL_SUBSCRIPTION_MUTATION = gengql(/* GraphQL */ `
  mutation cancelSub($subscriptionId: String!, $reason: String, $comments: String) {
    cancelSubscription(subscriptionId: $subscriptionId, reason: $reason, comments: $comments) {
      id
      status
      billingInterval
      quantity
      pricePerSeat {
        cents
        currency
      }
      totalPrice {
        cents
        currency
      }
      currentPeriodEnd
      trialEnd
      isTrialing
      daysUntilTrialEnd
      daysUntilRenewal
      cancelAtPeriodEnd
      isActive
    }
  }
`);

const CREATE_PORTAL_URL_MUTATION = gengql(/* GraphQL */ `
  mutation createSubscriptionPortalURL($accountId: String!, $path: String!) {
    createPortalURL(account_id: $accountId, path: $path)
  }
`);

// Status chip styling reads from the MUI palette (defined per theme in
// src/theme/index.ts). `bgcolor`/`color` are MUI sx tokens that resolve at
// render time, so this object adapts automatically to light/dark.
const STATUS_CHIP_STYLES: Record<string, { bgcolor: string; color: string }> = {
  success: { bgcolor: 'success.main', color: 'success.contrastText' },
  warning: { bgcolor: 'warning.main', color: 'warning.contrastText' },
  error: { bgcolor: 'error.main', color: 'error.contrastText' },
  default: { bgcolor: 'text.disabled', color: 'background.paper' },
};

function getStatusChipProps(sub: any) {
  const sx = (key: string) => STATUS_CHIP_STYLES[key] || STATUS_CHIP_STYLES.default;

  if (sub.cancelAtPeriodEnd) {
    return { label: `Cancels ${formatDate(sub.currentPeriodEnd)}`, sx: sx('default') };
  }
  switch (sub.status) {
    case SubscriptionStatus.Active:
      return { label: 'Active', sx: sx('success') };
    case SubscriptionStatus.Trialing: {
      const trialLabel =
        sub.daysUntilTrialEnd !== null && sub.daysUntilTrialEnd !== undefined
          ? `Trial \u2013 ${sub.daysUntilTrialEnd} days left`
          : 'Trial';
      return { label: trialLabel, sx: sx('warning') };
    }
    case SubscriptionStatus.PastDue:
      return { label: 'Past Due', sx: sx('error') };
    case SubscriptionStatus.Canceled:
      return { label: 'Canceled', sx: sx('default') };
    default:
      return { label: sub.status, sx: sx('default') };
  }
}

function formatPriceDisplay(sub: any): string {
  if (!sub.pricePerSeat) return '';
  const perSeat = money(sub.pricePerSeat);
  if (sub.billingInterval === BillingInterval.Year) {
    const monthlyEquiv = money({
      cents: Math.round(perSeat.cents / 12),
      currency: sub.pricePerSeat.currency,
    });
    return `${monthlyEquiv.formatted({ omitEmptyCents: true })}/month per seat (${perSeat.formatted({ omitEmptyCents: true })}/year)`;
  }
  return `${perSeat.formatted({ omitEmptyCents: true })}/month per seat`;
}

function formatAfterTrialPrice(sub: any): string {
  if (!sub.pricePerSeat) return '';
  const perSeat = money(sub.pricePerSeat);
  if (sub.billingInterval === BillingInterval.Year) {
    const monthlyEquiv = money({
      cents: Math.round(perSeat.cents / 12),
      currency: sub.pricePerSeat.currency,
    });
    return `${monthlyEquiv.formatted({ omitEmptyCents: true })}/mo per seat`;
  }
  return `${perSeat.formatted({ omitEmptyCents: true })}/mo per seat`;
}

function buildSwitchDialogContent(isMonthly: boolean, prices: any, currentPeriodEnd: any) {
  const mp = prices.monthly;
  const ap = prices.annual;
  if (!mp || !ap) return null;

  const monthlyPerMonth = money({ cents: mp.unitAmountCents, currency: mp.currency });
  const annualPerMonth = money({
    cents: Math.round(ap.unitAmountCents / 12),
    currency: ap.currency,
  });
  const monthlyPerYear = money({ cents: mp.unitAmountCents * 12, currency: mp.currency });
  const annualPerYear = money({ cents: ap.unitAmountCents, currency: ap.currency });
  const diff = monthlyPerYear.subtract(annualPerYear);
  const monthlyDiff = monthlyPerMonth.subtract(annualPerMonth);

  const current = isMonthly
    ? { label: 'Monthly', perMonth: monthlyPerMonth, perYear: monthlyPerYear }
    : { label: 'Annual', perMonth: annualPerMonth, perYear: annualPerYear };
  const next = isMonthly
    ? { label: 'Annual', perMonth: annualPerMonth, perYear: annualPerYear }
    : { label: 'Monthly', perMonth: monthlyPerMonth, perYear: monthlyPerYear };

  return (
    <div>
      <p className={s.switchPlanRow}>
        <strong>Current plan</strong>
        <br />
        {current.label} at {current.perMonth.formatted({ omitEmptyCents: true })}/mo per seat (
        {current.perYear.formatted({ omitEmptyCents: true })}/yr)
      </p>
      <p className={s.switchPlanRow}>
        <strong>New plan</strong>
        <br />
        {next.label} at {next.perMonth.formatted({ omitEmptyCents: true })}/mo per seat (
        {next.perYear.formatted({ omitEmptyCents: true })}/yr)
      </p>
      {isMonthly ? (
        <>
          <p className={s.switchSavings} style={{ color: darkGreen }}>
            You&apos;ll save {diff.formatted({ omitEmptyCents: true })} per year
          </p>
          <p className={s.switchDetail}>
            That&apos;s {monthlyDiff.formatted({ omitEmptyCents: true })}/mo less per seat. Change
            takes effect immediately.
          </p>
        </>
      ) : (
        <>
          <p className={s.switchSavings} style={{ color: red500 }}>
            You&apos;ll pay {diff.formatted({ omitEmptyCents: true })} more per year
          </p>
          <p className={s.switchDetail}>
            That&apos;s {monthlyDiff.formatted({ omitEmptyCents: true })}/mo extra per seat. Change
            takes effect at your next billing cycle ({formatDate(currentPeriodEnd)}).
          </p>
        </>
      )}
    </div>
  );
}

interface SubscriptionSettingsProps {
  account: any;
  isOwner?: boolean;
}

const SubscriptionSettings = ({
  account,
  isOwner = true,
}: SubscriptionSettingsProps): JSX.Element => {
  const [subscription, setSubscription] = useState(account.subscription);
  const transactions = account.transactions || [];
  const stripeReceipts = account.stripeReceipts || [];

  const [showSwitchDialog, setShowSwitchDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelStep, setCancelStep] = useState<'reason' | 'retain' | 'confirm'>('reason');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelComments, setCancelComments] = useState('');
  const [showManageSeats, setShowManageSeats] = useState(false);

  const { data: pricesData, loading: pricesLoading } = useQuery(GET_SUBSCRIPTION_PRICES, {
    skip: !showSwitchDialog,
  });

  const [updateInterval, { loading: switchLoading }] = useMutation(
    UPDATE_SUBSCRIPTION_INTERVAL_MUTATION
  );
  const [reactivateSub, { loading: reactivateLoading }] = useMutation(
    REACTIVATE_SUBSCRIPTION_MUTATION
  );
  const [cancelSub, { loading: cancelLoading }] = useMutation(CANCEL_SUBSCRIPTION_MUTATION);
  const [createPortal, { loading: portalLoading }] = useMutation(CREATE_PORTAL_URL_MUTATION);

  const isMonthly = subscription?.billingInterval === BillingInterval.Month;
  const statusChip = subscription ? getStatusChipProps(subscription) : null;
  const isPastDue = subscription?.status === SubscriptionStatus.PastDue;
  const isPendingCancel = subscription?.cancelAtPeriodEnd && subscription?.isActive;
  const isCanceled =
    subscription?.status === SubscriptionStatus.Canceled && !subscription?.isActive;
  const isViewer = !isOwner;
  const isDisabled = isPendingCancel || isCanceled || isViewer;

  const handleSwitchPlan = useCallback(async () => {
    const newInterval = isMonthly ? BillingInterval.Year : BillingInterval.Month;
    const { data } = await updateInterval({
      variables: { subscriptionId: subscription.id, interval: newInterval },
    });
    if (data?.updateSubscriptionInterval) {
      setSubscription(data.updateSubscriptionInterval);
      setShowSwitchDialog(false);
    }
  }, [isMonthly, subscription?.id, updateInterval]);

  const handleReactivate = useCallback(async () => {
    const { data } = await reactivateSub({
      variables: { subscriptionId: subscription.id },
    });
    if (data?.reactivateSubscription) {
      setSubscription(data.reactivateSubscription);
      setShowReactivateDialog(false);
    }
  }, [subscription?.id, reactivateSub]);

  const handleCancel = useCallback(async () => {
    const { data } = await cancelSub({
      variables: {
        subscriptionId: subscription.id,
        reason: cancelReason || undefined,
        comments: cancelComments || undefined,
      },
    });
    if (data?.cancelSubscription) {
      setSubscription(data.cancelSubscription);
      setShowCancelDialog(false);
      setCancelStep('reason');
      setCancelReason('');
      setCancelComments('');
    }
  }, [subscription?.id, cancelSub, cancelReason, cancelComments]);

  const handleUpdatePayment = useCallback(async () => {
    const { data } = await createPortal({
      variables: { accountId: account.id, path: window.location.pathname },
    });
    if (data?.createPortalURL) {
      window.open(data.createPortalURL, '_blank', 'noopener,noreferrer');
    }
  }, [account.id, createPortal]);

  if (!subscription) {
    return <div>No active subscription found.</div>;
  }

  const prices = pricesData?.subscriptionPrices;

  return (
    <div>
      {isPastDue && (
        <Alert severity="error" className={s.banner}>
          <div className={s.bannerText}>
            Payment failed. Please, update your payment method to avoid service interruption.
          </div>
          <div className={s.bannerAction}>
            <Button
              size="small"
              color="red"
              label="Update Payment Method"
              onClick={handleUpdatePayment}
            />
          </div>
        </Alert>
      )}
      {isPendingCancel && (
        <Alert severity="info" icon={false} className={`${s.banner} ${s.bannerGray}`}>
          <div className={s.bannerText}>
            Your subscription is set to cancel on {formatDate(subscription.currentPeriodEnd)}.
            You&apos;ll retain access until then.
          </div>
          <div className={s.bannerAction}>
            <Tooltip
              title="Subscription management requires an Owner seat."
              disableHoverListener={!isViewer}
            >
              <span>
                <Button
                  size="small"
                  color="teal"
                  label="Reactivate Subscription"
                  onClick={() => setShowReactivateDialog(true)}
                  disabled={isViewer}
                />
              </span>
            </Tooltip>
          </div>
        </Alert>
      )}
      {isCanceled && (
        <Alert severity="info" icon={false} className={`${s.banner} ${s.bannerGray}`}>
          <div className={s.bannerText}>
            Your subscription was deactivated on {formatDate(subscription.currentPeriodEnd)}.
          </div>
          <div className={s.bannerAction}>
            <Tooltip
              title="Subscription management requires an Owner seat."
              disableHoverListener={!isViewer}
            >
              <span>
                <Button
                  size="small"
                  color="teal"
                  label="Reactivate Subscription"
                  onClick={() => setShowManageSeats(true)}
                  disabled={isViewer}
                />
              </span>
            </Tooltip>
          </div>
        </Alert>
      )}

      <div className={s.details}>
        <div className={s.detailsRow}>
          <div className={s.detailsCol1}>Plan</div>
          <div className={s.detailsCol2}>{isMonthly ? 'Monthly' : 'Annual'}</div>
          <div className={s.detailsCol3}>
            <Tooltip
              title={isViewer ? 'Seat management requires an Owner seat.' : ''}
              disableHoverListener={!isViewer}
            >
              <span>
                <Button
                  size="small"
                  color="teal"
                  label="Manage Seats"
                  onClick={() => setShowManageSeats(true)}
                  disabled={isDisabled}
                />
              </span>
            </Tooltip>
          </div>
        </div>

        <div className={s.detailsRow}>
          <div className={s.detailsCol1}>Status</div>
          <div className={s.detailsCol2}>
            {statusChip && (
              <Chip
                label={statusChip.label}
                size="small"
                sx={{ ...statusChip.sx, borderRadius: '0.4rem' }}
              />
            )}
          </div>
        </div>

        <div className={s.detailsRow}>
          <div className={s.detailsCol1}>Price</div>
          <div className={s.detailsCol2}>{formatPriceDisplay(subscription)}</div>
        </div>

        <div className={s.detailsRow}>
          <div className={s.detailsCol1}>Seats</div>
          <div className={s.detailsCol2}>{subscription.quantity}</div>
        </div>

        <div className={s.detailsRow}>
          <div className={s.detailsCol1}>Next Billing</div>
          <div className={s.detailsCol2}>{formatDate(subscription.currentPeriodEnd)}</div>
        </div>

        {subscription.isTrialing && subscription.trialEnd && (
          <>
            <div className={s.detailsRow}>
              <div className={s.detailsCol1}>Trial Ends</div>
              <div className={s.detailsCol2}>{formatDate(subscription.trialEnd)}</div>
            </div>
            {subscription.pricePerSeat && (
              <div className={s.detailsRow}>
                <div className={s.detailsCol1}>After Trial</div>
                <div className={s.detailsCol2}>
                  {`${formatAfterTrialPrice(subscription)} `}
                  <span className={s.billedAuto}>(billed automatically)</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className={s.actions}>
        <Tooltip
          title="Subscription management requires an Owner seat."
          disableHoverListener={!isViewer}
        >
          <span style={isViewer ? { cursor: 'default' } : undefined}>
            <Link
              onClick={() => setShowSwitchDialog(true)}
              disabled={isDisabled}
              style={isViewer ? { cursor: 'default' } : { color: darkGreen }}
            >
              <span>{isMonthly ? 'Switch to Annual Plan' : 'Switch to Monthly Plan'}</span>
            </Link>
          </span>
        </Tooltip>
        <Tooltip
          title="Subscription management requires an Owner seat."
          disableHoverListener={!isViewer}
        >
          <span style={isViewer ? { cursor: 'default' } : undefined}>
            <Link
              onClick={handleUpdatePayment}
              disabled={portalLoading || isDisabled}
              style={isViewer ? { cursor: 'default' } : { color: darkGreen }}
            >
              Update Payment Method
            </Link>
          </span>
        </Tooltip>
        {isDisabled ? (
          <Tooltip
            title="Subscription management requires an Owner seat."
            disableHoverListener={!isViewer}
          >
            <span style={isViewer ? { cursor: 'default' } : undefined}>
              <Link
                onClick={
                  isViewer
                    ? undefined
                    : () => (isCanceled ? setShowManageSeats(true) : setShowReactivateDialog(true))
                }
                disabled={isViewer}
                style={isViewer ? { cursor: 'default' } : { color: darkGreen }}
              >
                Reactivate Subscription
              </Link>
            </span>
          </Tooltip>
        ) : (
          <Link onClick={() => setShowCancelDialog(true)} className={s.mutedLink}>
            Cancel Subscription
          </Link>
        )}
      </div>

      <ManageSeatsModal
        open={showManageSeats}
        mode={isCanceled ? 'activate' : 'manage'}
        subscription={subscription}
        seatAllocation={account.seatAllocation}
        onClose={() => setShowManageSeats(false)}
        onUpdate={setSubscription}
      />

      <Dialog
        open={showSwitchDialog}
        title={`Switch to ${isMonthly ? 'Annual' : 'Monthly'} Plan`}
        onConfirm={handleSwitchPlan}
        onCancel={() => setShowSwitchDialog(false)}
        confirmLabel={`Switch to ${isMonthly ? 'Annual' : 'Monthly'}`}
        disableSubmit={pricesLoading || !prices}
        submitting={switchLoading}
      >
        {pricesLoading ? (
          <p>Loading prices...</p>
        ) : (
          prices && buildSwitchDialogContent(isMonthly, prices, subscription.currentPeriodEnd)
        )}
      </Dialog>

      <Dialog
        open={showReactivateDialog}
        title="Reactivate Subscription"
        onConfirm={handleReactivate}
        onCancel={() => setShowReactivateDialog(false)}
        confirmLabel="Reactivate"
        submitting={reactivateLoading}
      >
        <p>
          <span>
            {`Your subscription will be reactivated and you will continue to be billed ${money(subscription.totalPrice).formatted({ omitEmptyCents: true })}/${isMonthly ? 'month' : 'year'} for ${subscription.quantity} ${subscription.quantity === 1 ? 'seat' : 'seats'}.`}
          </span>
        </p>
      </Dialog>

      <Dialog
        open={showCancelDialog}
        title={
          cancelStep === 'reason'
            ? "We're sorry to see you go"
            : cancelStep === 'retain'
              ? 'Cancel Subscription'
              : 'Confirm Cancellation'
        }
        warning={cancelStep !== 'reason'}
        onCancel={() => {
          if (cancelStep === 'retain') {
            setCancelStep('reason');
            return;
          }
          if (cancelStep === 'confirm') {
            setCancelStep('retain');
            return;
          }
          setShowCancelDialog(false);
          setCancelStep('reason');
          setCancelReason('');
          setCancelComments('');
        }}
        cancelLabel={cancelStep === 'reason' ? 'Close' : 'Go Back'}
        {...(cancelStep === 'reason'
          ? {
              onConfirm: () => setCancelStep('retain'),
              confirmLabel: 'Continue',
              disableSubmit: !cancelReason,
            }
          : cancelStep === 'confirm'
            ? {
                onConfirm: handleCancel,
                confirmLabel: 'I agree, cancel my subscription',
                submitting: cancelLoading,
                confirmButtonProps: { color: 'red' },
              }
            : {})}
      >
        {cancelStep === 'reason' ? (
          <div>
            <p className={s.reasonLabel}>Help us improve by sharing why you&apos;re canceling:</p>
            <select
              className={s.reasonSelect}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              aria-label="Cancellation reason"
            >
              <option value="">Select a reason...</option>
              <option value="not_using_enough">Not using it enough</option>
              <option value="too_expensive">Too expensive</option>
              <option value="project_ended">Project ended</option>
              <option value="switching_solution">Switching to another solution</option>
              <option value="missing_features">Missing features</option>
              <option value="expert_quality">Expert quality</option>
              <option value="company_downsizing">Company downsizing</option>
              <option value="temporary_need">Temporary need</option>
              <option value="other">Other</option>
            </select>
            <textarea
              className={s.reasonComments}
              placeholder="Tell us more (optional)"
              aria-label="Additional comments"
              value={cancelComments}
              onChange={(e) => setCancelComments(e.target.value)}
              rows={3}
            />
          </div>
        ) : cancelStep === 'retain' ? (
          <>
            <div className={s.retentionCard}>
              <div className={s.retentionTitle}>Talk to us</div>
              <p className={s.retentionDescription}>
                Our team may be able to help. Schedule a quick call to discuss your needs.
              </p>
              <Button
                color="teal"
                size="small"
                label="Schedule a Call"
                onClick={() => window.open(config.calendlyUrl, '_blank', 'noopener,noreferrer')}
              />
            </div>
            <div className={s.retentionDivider}>
              <Link onClick={() => setCancelStep('confirm')} className={s.mutedLink}>
                Skip and cancel subscription
              </Link>
            </div>
          </>
        ) : (
          <div>
            <p>By canceling, you agree to the following:</p>
            <ul className={s.cancelConfirmList}>
              <li>
                Your subscription will remain active until{' '}
                <strong>{formatDate(subscription.currentPeriodEnd)}</strong>.
              </li>
              <li>After that date, you will lose access to subscription features.</li>
              <li>You will not be charged again unless you reactivate.</li>
            </ul>
          </div>
        )}
      </Dialog>

      {stripeReceipts.length > 0 ? (
        <StripeReceiptList receipts={stripeReceipts} />
      ) : (
        <TransactionList
          transactions={transactions}
          amountColumn={{ header: 'Amount', field: 'money' }}
        />
      )}
    </div>
  );
};

export default SubscriptionSettings;
